import _ from 'lodash';
import T from '../localization/LocalizationKeys';

// TODO: type DtdlMetaProperty and put in DtdlContent ?

/**
 * @typedef {DtdlTelemetry|DtdlProperty|DtdlCommand|DtdlComponent|DtdlRelationship} DtdlContent
 */

/**
 * @typedef {{} & {
 * '@id': string,
 * '@type': string,
 * comment: string,
 * description: string,
 * displayName: string,
 * }} DtdlBase
*/


/**
 * @typedef {DtdlBase & {
*   '@context': string,
*   contents: DtdlContent[]
*   extends: DtdlInterface[],
*   schemas: ComplexSchemas[]
*  }} DtdlInterface
 */


/**
 * @typedef {DtdlBase & { name: string }} DtdlTypeBase
*/

/**
 * @typedef {DtdlTypeBase & { schema: Schemas, unit: string }} DtdlValueType
 */

/**
 * @typedef {PrimitiveSchemas|ComplexSchemas} Schemas
 */

/**
 * @typedef {DtdlTypeBase & {elementSchema: Schemas}} DtdlArray
 */

/**
 * @typedef {DtdlTypeBase & { enumValue: string|number}} DtdlEnumValue
*/

/**
 * @typedef {DtdlBase & {enumValues: DtdlEnumValue[], valueSchema: PrimitiveSchemas }} DtdlEnum
 */

/**
 * @typedef {DtdlTypeBase & { schema: string }} DtdlMapKey
 */

/**
 * @typedef {DtdlTypeBase & { schema: Schemas }} DtdlMapValue
 */

/**
 * @typedef {DtdlBase & { mapKey: DtdlMapKey, mapValue: DtdlMapValue }} DtdlMap
 */

/**
 * @typedef {DtdlValueType} DtdlField
*/

/**
 * @typedef {DtdlBase & { fields: DtdlField[] } } DtdlObject
*/

/**
 * @typedef {object} Property
 * @property {string} @type - Must be 'Property' or [Property, {@link semanticUnits}]
 * @property {string} name - identifier for the object
 * @property {string} schema - datatype of the object
 */

/**
 *
 * @typedef {DtdlValueType} DtdlTelemetry
 */

/**
 * @typedef {DtdlValueType & { writable: boolean }} DtdlProperty
 */

/**
 * @typedef {Property & { isMeta: boolean }} DtdlMetaProperty
 */

/**
 * @typedef {DtdlValueType} CommandRequest
*/

/**
 * @typedef {DtdlValueType} CommandResponse
 */

/**
 * @typedef {DtdlTypeBase & { request: CommandRequest, response: CommandResponse }} DtdlCommand
 */


/**
 * @typedef {DtdlTypeBase & {
 * maxMultiplicity: number,
 * minMultiplicity: number,
 * target: string,
 * writable: boolean,
 * properties: Property[]
 * }} DtdlRelationship

/**
 * @typedef {{
 * '@type': 'Component',
 * name: string,  // identifier for the object
 * schema: string, // datatype of the object
 * }} DtdlComponent
 */


const contentKeys = /** @type {const}*/(['Telemetry', 'Property', 'Command', 'Relationship', 'Component']);
const isExtended = obj => _.has(obj, 'extends');

/**
 *
 * @param {string[]} type - string or array of strings.
 * @returns {typeof contentKeys[number]}
 */
export const contentType = (type) => (_.isArray(type) ? _.intersection(type, contentKeys)[0] : type);

/**
 *
 * @param {typeof contentKeys[number][]} type Content type
 * @returns returns semanticType if available
 */
export const semanticType = (type) => {
  if (_.isArray(type)) {
    return _.intersection(type, _.keys(semanticUnits))[0];
  }
};

export const isMetaProperty = (type) => {
  if (_.isArray(type)) {
    return _.includes(type, 'MetaProperty');
  }
  return false;
};
/**
 * match content item against list of valid contentKeys
 *
 * @param {contentKeys[]} match - list of contentKeys to match against
 * @param {(DtdlTelemetry|DtdlProperty|DtdlCommand|DtdlComponent|DtdlRelationship)} c - object to match
 */
const matchTypefn = _.curry((match, c) => _.includes(match, contentType(c['@type'])));

/**
 * return a list of matched content
 * @param {contentKeys[]|contentKeys} match - contentKey or list of the same
 * @param {(DtdlTelemetry|DtdlProperty|DtdlCommand|DtdlComponent|DtdlRelationship)[]} data - list of mixed content
 */
const allMatchedContent = (match, data) => _.filter(data, matchTypefn(match));

/**
 * return a list of content that doesn't match
 * @param {contentKeys[]|contentKeys} match - contentKey or list of the same
 * @param {(DtdlTelemetry|DtdlProperty|DtdlCommand|DtdlComponent|DtdlRelationship)[]} data - list of mixed content
 */
const allButMatchedContent = (match, data) => _.reject(data, matchTypefn(match));


const filterContentType = _.curry((typeName, content) => {
  const type = contentType(content['@type']);
  return (type === typeName);
});

const filterContentName = _.curry((name, contents) => {
  if (name !== '') {
    return _.filter(contents, content => content.name === name);
  }
  return contents;

});

const filterSemanticType = _.curry((semantic, contents) => {
  if (semantic !== '') {
    return _.filter(contents, content => semanticType(content['@type']) === semantic);
  }
  return contents;

});

const findContent = (filterFn, components) => {
  return _.flatMap(_.map(components, comp => _.map(_.filter(comp.contents, filterFn), content => {
    return {
      ...content,
      component: comp.name
    };
  })));
};

const getFilteredComponents = (name, components) => (name !== '') ? _.filter(components, { name }) : components;

/**
 * @enum {string}
 */
export const ContentType = Object.freeze({
  TELEMETRY: 'telemetry',
  PROPERTIES: 'properties',
  COMMANDS: 'commands'
});


/**
 * parse takes a list of models, parses the models and returns a
 * json object with all models combined into one object.
 * all components are extended with a [component name] field that contains the parsed
 * component model.
 *
 * @param {DtdlInterface} model
 * @param {Array<DtdlInterface>} models list of dtdl models that is needed to build a object
 *
 * @returns json structure with all models combined
 */
// TODO: implement https://stackoverflow.com/questions/7891937/is-it-possible-to-implement-dynamic-getters-setters-in-javascript
// this makes it possible to naively do normal object request for something in contents
// this is to make the dtdl object more ofli a 'normal' json object
// but wait to implement until we know that we want to use it ke that...

export const parse = (model, models) => {

  const newModel = { ...model };
  // extend if needed,
  if (isExtended(model)) {
    const parentModel = getModel(model.extends, models);
    newModel.contents = [...newModel.contents, ...parentModel.contents];
  }
  // create list of all contents NOT components
  const list = allButMatchedContent(['Component'], newModel.contents);
  // iterate all components in contents and recurse
  const matchedContent = allMatchedContent('Component', newModel.contents);

  const createComponent = (component) => {
    const componentModel = getModel(component.schema, models);
    return { ...component, [component.name]: parse(componentModel, models) };
  };

  return { ...newModel, contents: [...list, ..._.map(matchedContent, createComponent)] };
};
/**
 * @typedef {object} GetContentByTypeProps
 * @property {ContentType} contentType
 * @property {DtdlInterface} dtdlModel
 * @property {string} filter
 * @property {string} component
 * @property {string} semanticType
 */

/**
 * Get all content from `dtdlModel` filtered by `filter` and `component`
 * @param {GetContentByTypeProps} props
 * @returns collection of contents of the correct type with fully qualified content name as key
 */
export const getContentByType = ({ contentType, dtdlModel, filter = '', component = '', semanticType = '' }) => {
  switch (contentType) {
    case ContentType.TELEMETRY:
      return getTelemetry({ dtdlModel, filter, component, semanticType });
    case ContentType.PROPERTIES:
      return getProperties({ dtdlModel, filter, component, semanticType });
    case ContentType.COMMANDS:
      return getCommands({ dtdlModel, filter, component, semanticType });
    default:
      break;
  }
};
/**
 * @typedef {object} GetTelemetryProps
 * @property {DtdlInterface} dtdlModel
 * @property {string} filter
 * @property {string} component
 * @property {string} semanticType
 */

/**
 * Get all telemetry from `dtdlModel` filtered
 * by `filter` and `component``
 *
 * @param {GetTelemetryProps} props
 * @returns {Array<DtdlTelemetry>} collection of telemetry with fully qualified telemetry name as key
 */
export const getTelemetry = ({ dtdlModel, filter = '', component = '', semanticType = '' }) => {
  const qualified = getFilteredComponents(component, getComponents({ model: dtdlModel }));
  let contents = findContent(filterContentType('Telemetry'), qualified);
  const filtered = filterContentName(filter, contents);
  return filterSemanticType(semanticType, filtered);
};

/**
 * @typedef {object} GetPropertiesProps
 * @property {DtdlInterface} dtdlModel
 * @property {string} filter
 * @property {string} component
 * @property {string} semanticType
 */

/**
 * Get all commands from `dtdlModel` filtered
 * by `filter` and `component`
 *
 * @param {GetPropertiesProps} props
 * @returns {Array<DtdlProperty>} collection of properties with fully qualified property name as key
 */
export const getProperties = ({ dtdlModel, filter = '', component = '', semanticType = '' }) => {
  const qualified = getFilteredComponents(component, getComponents({ model: dtdlModel }));
  const contents = findContent(filterContentType('Property'), qualified);
  const filtered = filterContentName(filter, contents);
  const remappedMetaProperty = _.map(filtered, (content) => {
    return {
      ...content,
      metaProperty: isMetaProperty(content['@type'])
    };
  });
  return filterSemanticType(semanticType, remappedMetaProperty);
};

/**
 * @typedef {object} GetCommandsProps
 * @property {DtdlInterface} dtdlModel
 * @property {string} filter
 * @property {string} component
 */

/**
 * Get all commands from `dtdlModel` filtered
 * by `filter` and `component`
 *
 * @param {GetCommandsProps} props
 * @returns {Array<DtdlCommand>} collection of commands with fully qualified command name as key
 */
export const getCommands = ({ dtdlModel, filter = '', component = '' }) => {
  const qualified = getFilteredComponents(component, getComponents({ model: dtdlModel }));
  const contents = findContent(filterContentType('Command'), qualified);
  return filterContentName(filter, contents);
};

/**
 * @typedef {object} GetSchemasProps
 * @property {DtdlInterface} dtdlModel
 */

/**
 * @param {GetSchemasProps} param0
 * @returns {ComplexSchemas[]}
 */
export const getSchemas = ({ dtdlModel }) => {
  return dtdlModel.schemas || [];
};
/**
 * @typedef {object} GetComponentsProps
 * @property {DtdlInterface} model
 * @property {string} [filter] - name of component. '.' marks root node
 * @property {string} [parent] - optional scoping
 * @property {boolean} [pruneRoot=false]
 */

/**
 * Get all component names from `dtdlModel` filtered
 * by `filter` and `component`
 *
 * @param {GetComponentsProps} props
 * @returns {Array<DtdlComponent>} collection of component names
 */
export const getComponents = ({ model, filter = '', parent = null, pruneRoot = false }) => {
  const qualifiedComponent = (comp) => {
    const name = parent != null ? `${parent}.${comp.name}` : comp.name;
    return {
      ...comp[comp.name],
      ...comp,
      name
    };
  };
  const rootComponent = (m) => {
    const contents = [
      ..._.filter(m.contents, filterContentType('Telemetry')),
      ..._.filter(m.contents, filterContentType('Property')),
      ..._.filter(m.contents, filterContentType('Command'))
    ];
    return {
      '@type': m['@type'],
      contents: contents,
      name: '.'
    };
  };
  let qualified = [];
  if (parent === null && pruneRoot === false) {
    qualified.push(rootComponent(model));
  }

  const components = _.filter(model?.contents, { '@type': 'Component' });
  qualified = [...qualified, ..._.map(components, qualifiedComponent)];
  const filtered = filter !== '' ? _.filter(qualified, { name: filter }) : qualified;
  if (_.isEmpty(filtered) && _.isEmpty(qualified)) {
    return [];
  }
  // return filtered but also recurse into qualified to make sure that deeper filters can be matched
  return _.flatMap(_.compact([...filtered, ..._.map(qualified, (comp) => getComponents({ model: comp, filter: filter, parent: comp.name }))]));
};

/**
 *
 * @param {string} id
 * @param {*} models
 * @returns {*} model
 */
export const getModel = (id, models) => {
  return _.find(models, { '@id': id });
};

/**
 * enum om dtdl primitive schemas
 * @typedef {('boolean'|date|dateTime|double|duration|float|integer|long|string|time)} PrimitiveSchemas
 *
*/
export const PRIMITIVE_SCHEMAS = Object.freeze({
  boolean: 'boolean',
  date: 'date',
  dateTime: 'dateTime',
  double: 'double',
  duration: 'duration',
  float: 'float',
  integer: 'integer',
  long: 'long',
  string: 'string',
  time: 'time'
});

/**
 * @typedef {(DtdlArray|DtdlEnum|DtdlMap|DtdlObject)} ComplexSchemas
 */
export const COMPLEX_SCHEMAS = Object.freeze({
  Array: 'Array',
  Enum: 'Enum',
  Map: 'Map',
  Object: 'Object'
});

/**
 * @type {object}
 * @constant
 */
export const semanticUnits = {
  Acceleration: {
    metrePerSecondSquared: 'metrePerSecondSquared',
    centimetrePerSecondSquared: 'centimetrePerSecondSquared',
    gForce: 'gForce'
  },
  Angle: {
    radian: 'radian',
    degreeOfArc: 'degreeOfArc',
    minuteOfArc: 'minuteOfArc',
    secondOfArc: 'secondOfArc'
  },
  AngularAcceleration: {
    radianPerSecondSquared: 'radianPerSecondSquared'
  },
  AngularVelocity: {
    radianPerSecond: 'radianPerSecond',
    degreePerSecond: 'degreePerSecond',
    revolutionPerSecond: 'revolutionPerSecond',
    revolutionPerMinute: 'revolutionPerMinute'
  },
  Area: {
    squareMetre: 'squareMetre',
    squareCentimetre: 'squareCentimetre',
    squareMillimetre: 'squareMillimetre',
    squareKilometre: 'squareKilometre',
    hectare: 'hectare',
    squareFoot: 'squareFoot',
    squareInch: 'squareInch',
    acre: 'acre'
  },
  Capacitance: {
    farad: 'farad',
    millifarad: 'millifarad',
    microfarad: 'microfarad',
    nanofarad: 'nanofarad',
    picofarad: 'picofarad'
  },
  Current: {
    ampere: 'ampere',
    microampere: 'microampere',
    milliampere: 'milliampere'
  },
  DataRate: {
    bitPerSecond: 'bitPerSecond',
    kibibitPerSecond: 'kibibitPerSecond',
    mebibitPerSecond: 'mebibitPerSecond',
    gibibitPerSecond: 'gibibitPerSecond',
    tebibitPerSecond: 'tebibitPerSecond',
    exbibitPerSecond: 'exbibitPerSecond',
    zebibitPerSecond: 'zebibitPerSecond',
    yobibitPerSecond: 'yobibitPerSecond',
    bytePerSecond: 'bytePerSecond',
    kibibytePerSecond: 'kibibytePerSecond',
    mebibytePerSecond: 'mebibytePerSecond',
    gibibytePerSecond: 'gibibytePerSecond',
    tebibytePerSecond: 'tebibytePerSecond',
    exbibytePerSecond: 'exbibytePerSecond',
    zebibytePerSecond: 'zebibytePerSecond',
    yobibytePerSecond: 'yobibytePerSecond'
  },
  DataSize: {
    bit: 'bit',
    kibibit: 'kibibit',
    mebibit: 'mebibit',
    gibibit: 'gibibit',
    tebibit: 'tebibit',
    exbibit: 'exbibit',
    zebibit: 'zebibit',
    yobibit: 'yobibit',
    byte: 'byte',
    kibibyte: 'kibibyte',
    mebibyte: 'mebibyte',
    gibibyte: 'gibibyte',
    tebibyte: 'tebibyte',
    exbibyte: 'exbibyte',
    zebibyte: 'zebibyte',
    yobibyte: 'yobibyte'
  },
  Density: {
    kilogramPerCubicMetre: 'kilogramPerCubicMetre',
    gramPerCubicMetre: 'gramPerCubicMetre'
  },
  Distance: {
    metre: 'metre',
    centimetre: 'centimetre',
    millimetre: 'millimetre',
    micrometre: 'micrometre',
    nanometre: 'nanometre',
    kilometre: 'kilometre',
    foot: 'foot',
    inch: 'inch',
    mile: 'mile',
    nauticalMile: 'nauticalMile',
    astronomicalUnit: 'astronomicalUnit'
  },
  ElectricCharge: {
    coulomb: 'coulomb'
  },
  Energy: {
    joule: 'joule',
    kilojoule: 'kilojoule',
    megajoule: 'megajoule',
    gigajoule: 'gigajoule',
    electronvolt: 'electronvolt',
    megaelectronvolt: 'megaelectronvolt',
    kilowattHour: 'kilowattHour'
  },
  Force: {
    newton: 'newton',
    pound: 'pound',
    ounce: 'ounce',
    ton: 'ton'
  },
  Frequency: {
    hertz: 'hertz',
    kilohertz: 'kilohertz',
    megahertz: 'megahertz',
    gigahertz: 'gigahertz'
  },
  Humidity: {
    kilogramPerCubicMetre: 'kilogramPerCubicMetre',
    gramPerCubicMetre: 'gramPerCubicMetre'
  },
  Illuminance: {
    lux: 'lux',
    footcandle: 'footcandle'
  },
  Inductance: {
    henry: 'henry',
    millihenry: 'millihenry',
    microhenry: 'microhenry'
  },
  Latitude: {
    radian: 'radian',
    degreeOfArc: 'degreeOfArc',
    minuteOfArc: 'minuteOfArc',
    secondOfArc: 'secondOfArc',
    turn: 'turn'
  },
  Longitude: {
    radian: 'radian',
    degreeOfArc: 'degreeOfArc',
    minuteOfArc: 'minuteOfArc',
    secondOfArc: 'secondOfArc',
    turn: 'turn'
  },
  Length: {
    metre: 'metre',
    centimetre: 'centimetre',
    millimetre: 'millimetre',
    micrometre: 'micrometre',
    nanometre: 'nanometre',
    kilometre: 'kilometre',
    foot: 'foot',
    inch: 'inch',
    mile: 'mile',
    nauticalMile: 'nauticalMile',
    astronomicalUnit: 'astronomicalUnit'
  },
  Luminance: {
    candelaPerSquareMetre: 'candelaPerSquareMetre'
  },
  Luminosity: {
    watt: 'watt',
    microwatt: 'microwatt',
    milliwatt: 'milliwatt',
    kilowatt: 'kilowatt',
    megawatt: 'megawatt',
    gigawatt: 'gigawatt',
    horsepower: 'horsepower',
    kilowattHourPerYear: 'kilowattHourPerYear'
  },
  LuminousFlux: {
    lumen: 'lumen'
  },
  LuminousIntensity: {
    candela: 'candela'
  },
  MagneticFlux: {
    weber: 'weber',
    maxwell: 'maxwell'
  },
  MagneticInduction: {
    tesla: 'tesla'
  },
  Mass: {
    kilogram: 'kilogram',
    gram: 'gram',
    milligram: 'milligram',
    microgram: 'microgram',
    tonne: 'tonne',
    slug: 'slug'
  },
  MassFlowRate: {
    gramPerSecond: 'gramPerSecond',
    kilogramPerSecond: 'kilogramPerSecond',
    gramPerHour: 'gramPerHour',
    kilogramPerHour: 'kilogramPerHour'
  },
  Power: {
    watt: 'watt',
    microwatt: 'microwatt',
    milliwatt: 'milliwatt',
    kilowatt: 'kilowatt',
    megawatt: 'megawatt',
    gigawatt: 'gigawatt',
    horsepower: 'horsepower',
    kilowattHourPerYear: 'kilowattHourPerYear'
  },
  Pressure: {
    pascal: 'pascal',
    kilopascal: 'kilopascal',
    bar: 'bar',
    millibar: 'millibar',
    millimetresOfMercury: 'millimetresOfMercury',
    poundPerSquareInch: 'poundPerSquareInch',
    inchesOfMercury: 'inchesOfMercury',
    inchesOfWater: 'inchesOfWater'
  },
  RelativeHumidity: {
    percent: 'percent'
  },
  Resistance: {
    ohm: 'ohm',
    milliohm: 'milliohm',
    kiloohm: 'kiloohm',
    megaohm: 'megaohm'
  },
  SoundPressure: {
    decibel: 'decibel',
    bel: 'bel'
  },
  Temperature: {
    kelvin: 'kelvin',
    degreeCelsius: 'degreeCelsius',
    degreeFahrenheit: 'degreeFahrenheit'
  },
  Thrust: {
    newton: 'newton',
    pound: 'pound',
    ounce: 'ounce',
    ton: 'ton'
  },
  TimeSpan: {
    second: 'second',
    millisecond: 'millisecond',
    microsecond: 'microsecond',
    nanosecond: 'nanosecond',
    minute: 'minute',
    hour: 'hour',
    day: 'day',
    year: 'year'
  },
  Torque: {
    newtonMetre: 'newtonMetre'
  },
  Velocity: {
    metrePerSecond: 'metrePerSecond',
    centimetrePerSecond: 'centimetrePerSecond',
    kilometrePerSecond: 'kilometrePerSecond',
    metrePerHour: 'metrePerHour',
    kilometrePerHour: 'kilometrePerHour',
    milePerHour: 'milePerHour',
    milePerSecond: 'milePerSecond',
    knot: 'knot'
  },
  Voltage: {
    volt: 'volt',
    millivolt: 'millivolt',
    microvolt: 'microvolt',
    kilovolt: 'kilovolt',
    megavolt: 'megavolt'
  },
  Volume: {
    cubicMetre: 'cubicMetre',
    cubicCentimetre: 'cubicCentimetre',
    litre: 'litre',
    millilitre: 'millilitre',
    cubicFoot: 'cubicFoot',
    cubicInch: 'cubicInch',
    fluidOunce: 'fluidOunce',
    gallon: 'gallon'
  },
  VolumeFlowRate: {
    litrePerSecond: 'litrePerSecond',
    millilitrePerSecond: 'millilitrePerSecond',
    litrePerHour: 'litrePerHour',
    millilitrePerHour: 'millilitrePerHour'
  }
};

export const TelemetryUnitsToLabel = {
  degreeCelsius: T.unit.temperature.celsius,
  degreeFahrenheit: T.unit.temperature.fahrenheit,
  millibar: T.unit.pressure.millibar,
  ppm: T.unit.concentration.ppm,
  percent: T.unit.relative.percentage,
  litre: T.unit.volume.litre,
  litrePerSecond: T.unit.volume.litrePerSecond,
  cubicMetre: T.unit.volume.cubicmetre,
  kilowatt: T.unit.power.kilowatt,
  metre: T.unit.length.metre
};

export const formatTelemetryUnit = (unit) => {
  if (!unit) {
    return '';
  }
  if (!TelemetryUnitsToLabel[unit]) {
    // console.warn(`Missing unit string for ${unit}`);
    return unit;
  }
  return TelemetryUnitsToLabel[unit];
};
